import { Controller } from "@hotwired/stimulus"

// Single shared confirmation dialog for quick-approval triggers on the
// configurable timesheets timeline. Buttons elsewhere on the page emit
// `click->quick-approve#open` actions with Stimulus params describing the
// scope (employee_week | fleet_week | day) and labels; this controller
// populates the dialog and submits the same hidden form for all scopes.
export default class extends Controller {
  static targets = [
    "dialog",
    "title",
    "scopeDescription",
    "countDescription",
    "scopeInput",
    "employeeIdInput",
    "fleetIdInput",
    "approveDateInput",
  ]

  static values = {
    countLabelOne: String,
    countLabelOther: String,
  }

  connect() {
    this.boundHandleSubmitEnd = this.handleSubmitEnd.bind(this)
    if (this.hasDialogTarget) {
      this.dialogTarget.addEventListener("turbo:submit-end", this.boundHandleSubmitEnd)
    }
  }

  disconnect() {
    if (this.hasDialogTarget && this.boundHandleSubmitEnd) {
      this.dialogTarget.removeEventListener("turbo:submit-end", this.boundHandleSubmitEnd)
    }
  }

  open(event) {
    const params = event.params || {}
    const scope = params.scope || ""
    const count = parseInt(params.count, 10) || 0
    const scopeText = params.scopeLabel || ""
    const titleText = params.title || ""

    if (this.hasTitleTarget && titleText) {
      this.titleTarget.textContent = titleText
    }
    if (this.hasScopeDescriptionTarget) {
      this.scopeDescriptionTarget.textContent = scopeText
    }
    if (this.hasCountDescriptionTarget) {
      this.countDescriptionTarget.textContent = this.formatCountLabel(count)
    }

    if (this.hasScopeInputTarget) {
      this.scopeInputTarget.value = scope
    }
    if (this.hasEmployeeIdInputTarget) {
      this.employeeIdInputTarget.value = params.employeeId || ""
    }
    if (this.hasFleetIdInputTarget) {
      this.fleetIdInputTarget.value = params.fleetId || ""
    }
    if (this.hasApproveDateInputTarget) {
      this.approveDateInputTarget.value = params.approveDate || ""
    }

    if (this.hasDialogTarget) {
      if (this.dialogTarget.open) {
        this.dialogTarget.close()
      }
      this.dialogTarget.showModal()
      document.body.style.overflow = "hidden"
    }
  }

  close() {
    if (this.hasDialogTarget && this.dialogTarget.open) {
      this.dialogTarget.close()
      document.body.style.overflow = ""
    }
  }

  // Close the dialog when the form has been submitted via Turbo. We do this
  // regardless of success/failure — the flash message communicates the result.
  handleSubmitEnd() {
    this.close()
  }

  // Click on the dialog backdrop closes the dialog.
  handleBackdropClick(event) {
    if (event.target === this.dialogTarget) {
      this.close()
    }
  }

  formatCountLabel(count) {
    const template = count === 1 ? this.countLabelOneValue : this.countLabelOtherValue
    if (!template) return String(count)
    return template.replace("%{count}", String(count))
  }
}
